<template>
  <PageHeader>
    <template #header-title>{{ $t('Candidates') }}</template>
    <template v-if="!candidatesLoadingError && !loading" #header-actions>
      <AddRecruiterFreelancer
        v-if="user.hasPermission('manage-candidates')"
        :currency="currency"
        :manages-workers="managesWorkers"
      />
    </template>
  </PageHeader>
  <div>
    <div v-if="candidatesLoadingError" class="tw-mt-9">
      <ContentLoadingErrorNotice />
    </div>
    <template v-else>
      <WdsAlert v-if="managesWorkers" info icon class="tw-mb-4">
        {{
          $t(
            'Workers currently engaged in a contractual relationship with you will be subject to the terms and conditions of the existing agreement for the remainder of the contract’s attribution period',
          )
        }}
      </WdsAlert>

      <WdsAlert
        v-if="filterPendingActionsOnly"
        info
        icon
        class="tw-mb-4"
        data-test="recruiter-candidates-pending-filter-banner"
      >
        <WdsStack direction-row align-center justify-space-between gap-sm>
          <span>{{ $t('Showing only candidates with pending actions.') }}</span>
          <WdsButtonGhost
            secondary
            sm
            data-test="recruiter-candidates-clear-pending-filter"
            @click="clearPendingActionsFilter"
          >
            {{ $t('Show all candidates') }}
          </WdsButtonGhost>
        </WdsStack>
      </WdsAlert>

      <div>
        <RecruiterFreelancerContacts
          :candidates="result?.recruiterCandidates.data"
          :apollo-loading="loading"
          :filtered="filterPendingActionsOnly"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { WdsAlert, WdsButtonGhost, WdsStack } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import AddRecruiterFreelancer from '@/components/Contacts/RecruiterFreelancer/AddRecruiterFreelancer.vue'
import RecruiterFreelancerContacts from '@/components/Contacts/RecruiterFreelancer/RecruiterFreelancerContacts.vue'
import { GetRecruiterCandidatesQuery } from '@/components/Contacts/RecruiterFreelancer/graphql/queries'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { user } from '@/composables/useUser'
import PageHeader from '@/components/PageHeader.vue'

defineProps({
  managesWorkers: {
    type: Boolean,
    required: true,
  },
})

const candidatesLoadingError = ref(false)
const currency = computed(() => currentAccount.currency)

// Read ?action=needed for the dashboard tile click-through.
const initialUrl = new URL(window.location.href)
const filterPendingActionsOnly = ref(
  initialUrl.searchParams.get('action') === 'needed',
)

// Scope to the active account.
const queryVariables = computed(() => ({
  accounts: [currentAccount.globalId],
  first: PER_PAGE_MAXIMUM,
  hasPendingActions: filterPendingActionsOnly.value ? true : null,
}))

const {
  result,
  loading,
  onError: onCandidatesLoadingError,
} = useQuery(
  GetRecruiterCandidatesQuery,
  queryVariables,
  {
    clientId: 'publicApi',
  },
)

const clearPendingActionsFilter = () => {
  filterPendingActionsOnly.value = false
  const url = new URL(window.location.href)
  url.searchParams.delete('action')
  window.history.replaceState({}, '', url.toString())
}

onCandidatesLoadingError((error) => {
  candidatesLoadingError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})
</script>
